import React, { useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import { useFormik, getIn } from 'formik';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';

import { formatNumberThsndAndDec } from '../helpers/stringFormat';
import { formatString } from './valueFunctions';
import { Typography } from '@mui/material';
import { GreenRedCompactSwitch, TextFieldCustom } from '../helpers/formFields';
import { tooltip, generalTooltipText } from '../helpers/tooltip';


const RiskValueDriverRegulatoryForm = forwardRef(({ currency, outcome, outcomeList, riskStructure, editRegulatoryDriverId, editRegulatoryDriver, formActions, onSubmit }, ref) => {
    const [open, setOpen] = useState(false);
    const [openOutcome, setOpenOutcome] = useState(false);
    const [selectedType, setSelectedType] = useState(riskStructure.types[0].type || '')
    const [positiveDriver, setPositiveDriver] = useState(true);

    const tooltipText = {
        riskChange: `Risk ${positiveDriver ? "Reduction" : "Increase"} when using your product instead of the competitive alternative.`,
    }

    let riskStructureSelected = riskStructure.types.find(type => type.type === selectedType)

    const handleTypeChange = (event) => {
        setSelectedType(event.target.value);

    };

    const handleOutcomeChange = (event) => {
        formik.setFieldValue('outcome', event.target.value)
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleOutcomeClose = () => {
        setOpenOutcome(false);
    };

    const handleOutcomeOpen = () => {
        setOpenOutcome(true);
    };

    useEffect(() => {
        setSelectedType(riskStructure.types[0].type)
        setPositiveDriver(true)

        formik.setFieldValue('sub_type', riskStructure.types[0].type)
        formik.setFieldValue("fine_value", formatNumberThsndAndDec(riskStructure.types[0].range.lower_value))
        formik.setFieldValue("likelihood_violation", riskStructure.types[0].likelihood.violation.value)
        formik.setFieldValue("likelihood_fine", riskStructure.types[0].likelihood.fine.value)
        formik.setFieldValue("multiple", Object.keys(riskStructure.types[0].calculation_details.calculation)[0])
        formik.setFieldValue("multiple_value", 0)
        formik.setFieldValue("rate", Object.keys(riskStructure.types[0].calculation_details.calculation)[1])
        formik.setFieldValue("rate_value", Object.values(riskStructure.types[0].calculation_details.calculation)[1]['value'])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [riskStructure]);

    useEffect(() => {
            formik.setFieldValue("outcome", outcomeList[0])
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [outcomeList]);

    useEffect(() => {
        if (editRegulatoryDriverId) {
            setPositiveDriver(editRegulatoryDriver["positive_driver"])
            setSelectedType(editRegulatoryDriver["sub_type"])

            formik.setFieldValue("sub_type", editRegulatoryDriver["sub_type"])
            formik.setFieldValue("fine_value", formatNumberThsndAndDec(editRegulatoryDriver["fine_value"]))
            formik.setFieldValue("likelihood_violation", editRegulatoryDriver["likelihood_violation"])
            formik.setFieldValue("likelihood_fine", editRegulatoryDriver["likelihood_fine"])
            formik.setFieldValue("multiple", editRegulatoryDriver["multiple"])
            formik.setFieldValue("multiple_value", editRegulatoryDriver["multiple_value"])
            formik.setFieldValue("rate", editRegulatoryDriver["rate"])
            formik.setFieldValue("rate_value", editRegulatoryDriver["rate_value"])
            formik.setFieldValue("risk_reduction", editRegulatoryDriver["risk_reduction"])
            formik.setFieldValue("positive_driver", editRegulatoryDriver["positive_driver"])
            formik.setFieldValue("outcome", editRegulatoryDriver["outcome"])
            formik.setFieldValue("driver_name", editRegulatoryDriver["driver_name"])
            formik.setFieldValue("comment", editRegulatoryDriver["comment"])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editRegulatoryDriverId])

    useEffect(() => {
        if (!editRegulatoryDriverId) {
            formik.setFieldValue("driver_name", `${selectedType} Risk ${positiveDriver ? 'Reduction' : 'Increase'}`)

            formik.setFieldValue('sub_type', selectedType)
            formik.setFieldValue("fine_value", formatNumberThsndAndDec(riskStructureSelected.range.lower_value))
            formik.setFieldValue("likelihood_violation", riskStructureSelected.likelihood.violation.value)
            formik.setFieldValue("likelihood_fine", riskStructureSelected.likelihood.fine.value)
            formik.setFieldValue("multiple", Object.keys(riskStructureSelected.calculation_details.calculation)[0])
            formik.setFieldValue("multiple_value", 0)
            formik.setFieldValue("rate", Object.keys(riskStructureSelected.calculation_details.calculation)[1])
            formik.setFieldValue("rate_value", Object.values(riskStructureSelected.calculation_details.calculation)[1]['value'])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positiveDriver, selectedType])

    const initialValues = {
        id: null,
        risk_type: 'regulatory',
        outcome: '',
        driver_name: `${selectedType} Risk ${positiveDriver ? 'Reduction' : 'Increase'}`,
        comment: '',
        positive_driver: true,
        type: riskStructure.types[0].type,
        currency: currency,
        sub_type: selectedType,
        fine_value: formatNumberThsndAndDec(riskStructure.types[0].range.lower_value),
        likelihood_violation: riskStructure.types[0].likelihood.violation.value,
        likelihood_fine: riskStructure.types[0].likelihood.fine.value,
        multiple: Object.keys(riskStructure.types[0].calculation_details.calculation)[0],
        multiple_value: 0,
        rate: Object.keys(riskStructure.types[0].calculation_details.calculation)[1],
        rate_value: Object.values(riskStructure.types[0].calculation_details.calculation)[1]['value'],
        risk_reduction: 0,
    }

    const isNumeric = (value) => {
        return /^-?\d{1,3}(,\d{3})*(\.\d+)?$/.test(value);
    };

    const isPositive = (value) => {
        return parseFloat(value.toString().replace(/,/g, '')) >= 0;
    };

    const validate = (values) => {
        const errors = {};

        if (!values.driver_name) {
            errors.driver_name = 'Driver must have a name';
        }
        
        if (!isNumeric(values.fine_value)) {
            errors.fine_value = 'Must be numeric value';
        } else if (!isPositive(values.fine_value)) {
            errors.fine_value = 'Must be positive or zero';
        }

        if (!isNumeric(values.rate_value)) {
            errors.rate_value = 'Must be numeric value';
        } else if (!isPositive(values.rate_value)) {
            errors.rate_value = 'Must be positive or zero';
        } else if (values.rate_value > 100) {
            errors.rate_value = 'Not more than 100';
        }

        if (!isNumeric(values.likelihood_violation)) {
            errors.likelihood_violation = 'Must be numeric value';
        } else if (!isPositive(values.likelihood_violation)) {
            errors.likelihood_violation = 'Must be positive or zero';
        } else if (values.likelihood_violation > 100) {
            errors.likelihood_violation = 'Not more than 100';
        }

        if (!isNumeric(values.likelihood_fine)) {
            errors.likelihood_fine = 'Must be numeric value';
        } else if (!isPositive(values.likelihood_fine)) {
            errors.likelihood_fine = 'Must be positive or zero';
        } else if (values.likelihood_fine > 100) {
            errors.likelihood_fine = 'Not more than 100';
        }

        if (!isNumeric(values.risk_reduction)) {
            errors.risk_reduction = 'Must be numeric value';
        } else if (!isPositive(values.risk_reduction)) {
            errors.risk_reduction = 'Must be positive or zero';
        }

        return errors
    }

    const formik = useFormik({
        initialValues: initialValues,
        validate: validate,
        onSubmit: onSubmit,
    });

    useImperativeHandle(ref, () => ({
        resetForm() {
            formik.handleReset()
            setSelectedType(riskStructure.types[0].type)
            setPositiveDriver(true)
            if (!outcome) {
                formik.setFieldValue("outcome", outcomeList[0] || '')
            }
            formik.setFieldValue("fine_value", formatNumberThsndAndDec(riskStructure.types[0].range.lower_value))
            formik.setFieldValue("likelihood_violation", riskStructure.types[0].likelihood.violation.value)
            formik.setFieldValue("likelihood_fine", riskStructure.types[0].likelihood.fine.value)
            formik.setFieldValue("multiple", Object.keys(riskStructure.types[0].calculation_details.calculation)[0])
            formik.setFieldValue("multiple_value", 0)
            formik.setFieldValue("rate", Object.keys(riskStructure.types[0].calculation_details.calculation)[1])
            formik.setFieldValue("rate_value", Object.values(riskStructure.types[0].calculation_details.calculation)[1]['value'])
            formik.setFieldValue("risk_reduction", 0)
        },
    }));

    return (
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ ml: 2, maxWidth: 1200 }}>
            <Grid container columnSpacing={2} rowSpacing={2} sx={{ mt: 1 }}>
                {riskStructure.types.map((type, index) => {
                    if (type.type === selectedType) {
                        return (
                            <React.Fragment key={index}>
                                <Grid item xs={12}>
                                    <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                            2. Select Violation
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={2}>
                                    <Stack direction="column" gap={1}>
                                        <Typography variant="caption">
                                            Violation type
                                        </Typography>
                                        <Box>
                                            <Select
                                                open={open}
                                                onClose={handleClose}
                                                onOpen={handleOpen}
                                                value={formik.values.sub_type}
                                                onChange={handleTypeChange}
                                                size='small'
                                            >
                                                {riskStructure.types.map((type, index) => (
                                                    <MenuItem key={index} value={type.type}>
                                                        {type.type}{tooltip(type.description)}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </Box>
                                    </Stack>
                                </Grid>
                                <Grid item xs={10} />
                                <Grid item xs={12}>
                                    <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                            3. Calculations
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={2}>
                                    <Stack direction="column">
                                        <Typography variant="caption" sx={{ mr: 1 }}>
                                            {formatString(Object.keys(type.calculation_details.calculation)[1])}{tooltip(Object.values(type.calculation_details.calculation)[1]['description'])}
                                        </Typography>
                                        <TextFieldCustom
                                            name="rate_value"
                                            endAdornment={
                                                <Typography variant="caption">
                                                    %
                                                </Typography>
                                            }
                                            value={formik.values.rate_value}
                                            onChange={(event) => {
                                                const { value } = event.target
                                                const formattedValue = formatNumberThsndAndDec(value)
                                                formik.setFieldValue("rate_value", formattedValue || '')
                                            }}
                                            onBlur={formik.handleBlur}
                                            error={getIn(formik.errors, "rate_value") && Boolean(getIn(formik.errors, "rate_value"))}
                                            helperText={getIn(formik.touched, "rate_value") && getIn(formik.errors, "rate_value")}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={10} />
                                <Grid item xs={2}>
                                    <Stack direction="column">
                                        <Typography variant="caption" sx={{ mr: 1 }}>
                                            Likelihood of Violation{tooltip(type.likelihood.violation.description)}
                                        </Typography>
                                        <TextFieldCustom
                                            name="likelihood_violation"
                                            endAdornment={
                                                <Typography variant="caption">
                                                    %
                                                </Typography>
                                            }
                                            value={formik.values.likelihood_violation}
                                            onChange={(event) => {
                                                const { value } = event.target
                                                const formattedValue = formatNumberThsndAndDec(value)
                                                formik.setFieldValue("likelihood_violation", formattedValue || '')
                                            }}
                                            onBlur={formik.handleBlur}
                                            error={getIn(formik.errors, "likelihood_violation") && Boolean(getIn(formik.errors, "likelihood_violation"))}
                                            helperText={getIn(formik.touched, "likelihood_violation") && getIn(formik.errors, "likelihood_violation")}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={2}>
                                    <Stack direction="column">
                                        <Typography variant="caption" sx={{ mr: 1 }}>
                                            Likelihood of Fine{tooltip(type.likelihood.fine.description)}
                                        </Typography>
                                        <TextFieldCustom
                                            name="likelihood_fine"
                                            endAdornment={
                                                <Typography variant="caption">
                                                    %
                                                </Typography>
                                            }
                                            value={formik.values.likelihood_fine}
                                            onChange={(event) => {
                                                const { value } = event.target
                                                const formattedValue = formatNumberThsndAndDec(value)
                                                formik.setFieldValue("likelihood_fine", formattedValue || '')
                                            }}
                                            onBlur={formik.handleBlur}
                                            error={getIn(formik.errors, "likelihood_fine") && Boolean(getIn(formik.errors, "likelihood_fine"))}
                                            helperText={getIn(formik.touched, "likelihood_fine") && getIn(formik.errors, "likelihood_fine")}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={8} />
                                <Grid item xs={2}>
                                    <Stack direction="column">
                                        <Typography variant="caption" sx={{ mr: 1 }}>
                                            Fine Value
                                        </Typography>
                                        <TextFieldCustom
                                            name="fine_value"
                                            startAdornment={
                                                <Typography variant="caption">
                                                    {type.range.currency}
                                                </Typography>
                                            }
                                            value={formik.values.fine_value}
                                            onChange={(event) => {
                                                const { value } = event.target
                                                const formattedValue = formatNumberThsndAndDec(value)
                                                formik.setFieldValue("fine_value", formattedValue || '')
                                            }}
                                            onBlur={formik.handleBlur}
                                            error={getIn(formik.errors, "fine_value") && Boolean(getIn(formik.errors, "fine_value"))}
                                            helperText={getIn(formik.touched, "fine_value") && getIn(formik.errors, "fine_value")}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid
                                    item
                                    xs={2}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-end',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                        Fines vary from {type.range.currency}{formatNumberThsndAndDec(type.range.lower_value)} to {type.range.currency}{formatNumberThsndAndDec(type.range.upper_value)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={8} />
                                <Grid item xs={2}>
                                    <Stack direction="column">
                                        <Typography variant="caption" sx={{ mr: 1 }}>
                                            {`Risk ${positiveDriver ? 'Reduction' : 'Increase'}`}{tooltip(tooltipText.riskChange)}
                                        </Typography>
                                        <TextFieldCustom
                                            name="risk_reduction"
                                            endAdornment={
                                                <Typography variant="caption">
                                                    %
                                                </Typography>
                                            }
                                            value={formik.values.risk_reduction}
                                            onChange={(event) => {
                                                const { value } = event.target
                                                const formattedValue = formatNumberThsndAndDec(value)
                                                formik.setFieldValue("risk_reduction", formattedValue || '')
                                            }}
                                            onBlur={formik.handleBlur}
                                            error={getIn(formik.errors, "risk_reduction") && Boolean(getIn(formik.errors, "risk_reduction"))}
                                            helperText={getIn(formik.touched, "risk_reduction") && getIn(formik.errors, "risk_reduction")}
                                            greenBackground={positiveDriver}
                                            redBackground={!positiveDriver}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={10} />
                                <Grid item xs={12}>
                                    <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                            4. Driver details
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={2}>
                                    <Stack direction="column" gap={1}>
                                        <Typography variant="caption" sx={{ mr: 1 }}>
                                            Value driver{tooltip(generalTooltipText.differentiationValue)}
                                        </Typography>
                                        <GreenRedCompactSwitch
                                            name="positive_driver"
                                            leftLabel="Negative"
                                            rightLabel="Positive"
                                            value={formik.values.positive_driver}
                                            onChange={(event) => {
                                                formik.handleChange(event)
                                                setPositiveDriver(!positiveDriver)
                                            }}
                                        />

                                    </Stack>
                                </Grid>
                                <Grid item xs={10} />
                                {!outcome &&
                                    (<React.Fragment>
                                        <Grid item xs={2}>
                                            <Stack direction="column" gap={1}>
                                                <Typography variant="caption">
                                                    Outcome
                                                </Typography>
                                                <Box>
                                                    <Select
                                                        open={openOutcome}
                                                        onClose={handleOutcomeClose}
                                                        onOpen={handleOutcomeOpen}
                                                        value={formik.values.outcome}
                                                        onChange={handleOutcomeChange}
                                                        size='small'
                                                    >
                                                        {outcomeList.map((outcome) => (
                                                            <MenuItem key={outcome} value={outcome}>
                                                                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                                                    {outcome}
                                                                </Box>
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </Box>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={10} />
                                    </React.Fragment>)
                                }
                                <Grid item xs={12}>
                                    <Grid container columnSpacing={2}>
                                        <Grid item xs={3}>
                                            <Typography variant="caption" sx={{ mr: 1 }}>
                                                Name of driver
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={5}>
                                            <Typography variant="caption" sx={{ mr: 1 }}>
                                                Comment
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={3} />
                                        <Grid item xs={3}>
                                            <TextFieldCustom
                                                name="driver_name"
                                                value={formik.values.driver_name}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={getIn(formik.errors, "driver_name") && Boolean(getIn(formik.errors, "driver_name"))}
                                                helperText={getIn(formik.touched, "driver_name") && getIn(formik.errors, "driver_name")}
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={5}>
                                            <TextFieldCustom
                                                name="comment"
                                                value={formik.values.comment}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={getIn(formik.errors, "comment") && Boolean(getIn(formik.errors, "comment"))}
                                                helperText={getIn(formik.touched, "comment") && getIn(formik.errors, "comment")}
                                                fullWidth
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </React.Fragment>
                        )
                    }
                    else {
                        return null;
                    }
                })
                }
            </Grid>
            <Box sx={{ mt: 3, mb: 4 }}>
                {formActions()}
            </Box>
        </Box>
    )

})

export default RiskValueDriverRegulatoryForm                